/**
 * Natural Language Frequency Schedule Parser
 * Parses natural language frequency expressions into actionable calendar schedules.
 *
 * Supported patterns:
 * - Day intervals: "every 3 days", "every four days", "every 7th day", "every third day", "3 days apart", "e3d", "q4d", "eod"
 * - Week intervals: "every 2 weeks", "every two weeks", "bi-weekly", "every other week", "q2w"
 * - On/Off cycles: "5 days on, 2 days off", "5 on 2 off", "4 on 3 off", "two days on, one day off"
 * - Days of week: "Mon, Wed, Fri", "Weekly (Tuesday AM)", "Twice Weekly (Mon / Thu)"
 * - Presets: "Weekdays (Mon-Fri)", "Weekends (Sat-Sun)", "Daily", "PRN / As Needed"
 */

// Word to number lookup covering cardinal and ordinal forms
export const WORD_TO_NUMBER = {
  zero: 0,
  one: 1,
  first: 1,
  '1st': 1,
  two: 2,
  second: 2,
  '2nd': 2,
  three: 3,
  third: 3,
  '3rd': 3,
  four: 4,
  fourth: 4,
  '4th': 4,
  five: 5,
  fifth: 5,
  '5th': 5,
  six: 6,
  sixth: 6,
  '6th': 6,
  seven: 7,
  seventh: 7,
  '7th': 7,
  eight: 8,
  eighth: 8,
  '8th': 8,
  nine: 9,
  ninth: 9,
  '9th': 9,
  ten: 10,
  tenth: 10,
  '10th': 10,
  eleven: 11,
  eleventh: 11,
  '11th': 11,
  twelve: 12,
  twelfth: 12,
  '12th': 12,
  thirteen: 13,
  thirteenth: 13,
  '13th': 13,
  fourteen: 14,
  fourteenth: 14,
  '14th': 14,
  fifteen: 15,
  fifteenth: 15,
  '15th': 15,
  sixteen: 16,
  sixteenth: 16,
  '16th': 16,
  seventeen: 17,
  seventeenth: 17,
  '17th': 17,
  eighteen: 18,
  eighteenth: 18,
  '18th': 18,
  nineteen: 19,
  nineteenth: 19,
  '19th': 19,
  twenty: 20,
  twentieth: 20,
  '20th': 20,
  'twenty-one': 21,
  'twenty one': 21,
  '21st': 21,
  'twenty-two': 22,
  'twenty two': 22,
  '22nd': 22,
  'twenty-three': 23,
  'twenty three': 23,
  '23rd': 23,
  'twenty-four': 24,
  'twenty four': 24,
  '24th': 24,
  'twenty-five': 25,
  'twenty five': 25,
  '25th': 25,
  'twenty-six': 26,
  'twenty six': 26,
  '26th': 26,
  'twenty-seven': 27,
  'twenty seven': 27,
  '27th': 27,
  'twenty-eight': 28,
  'twenty eight': 28,
  '28th': 28,
  'twenty-nine': 29,
  'twenty nine': 29,
  '29th': 29,
  thirty: 30,
  thirtieth: 30,
  '30th': 30,
  'thirty-one': 31,
  'thirty one': 31,
  '31st': 31,
  forty: 40,
  'forty-five': 45,
  'forty five': 45,
  sixty: 60,
  ninety: 90
};

/**
 * Resolves a token (string number, ordinal, or word) into a positive integer.
 * e.g. "3", "3rd", "four", "fourth", "14th", "fourteen" -> number
 */
export function parseNumberToken(token) {
  if (!token) return null;
  let clean = token.toLowerCase().trim();

  // Strip trailing -day, -days, -d, -week, -weeks, etc. if captured together
  clean = clean.replace(/-(?:days?|d|weeks?|w)$/, '');

  // Direct lookup
  if (WORD_TO_NUMBER[clean] !== undefined) {
    return WORD_TO_NUMBER[clean];
  }

  // Remove common ordinal suffixes: 1st -> 1, 2nd -> 2, 3rd -> 3, 4th -> 4
  const numMatch = clean.match(/^(\d+)(?:st|nd|rd|th)?$/);
  if (numMatch) {
    const val = parseInt(numMatch[1], 10);
    return isNaN(val) ? null : val;
  }

  return null;
}

/**
 * Computes exact integer day difference between two Date objects,
 * using UTC calendar midnight to prevent any daylight saving time (DST) distortions.
 */
export function getDayDifference(dateA, dateB) {
  if (!dateA || !dateB) return 0;
  const d1 = new Date(dateA);
  const d2 = new Date(dateB);
  const utc1 = Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate());
  const utc2 = Date.UTC(d2.getFullYear(), d2.getMonth(), d2.getDate());
  return Math.round((utc2 - utc1) / (1000 * 60 * 60 * 24));
}

/**
 * Determines anchor date for a protocol.
 * Priority: protocol.startDate > protocol.createdAt > today
 */
export function getProtocolAnchorDate(protocol) {
  if (protocol?.startDate) {
    // startDate is in YYYY-MM-DD
    const parts = protocol.startDate.split('-').map(Number);
    if (parts.length === 3 && !isNaN(parts[0])) {
      return new Date(parts[0], parts[1] - 1, parts[2]);
    }
  }

  if (protocol?.createdAt) {
    // Firestore Timestamp or ISO string or Date
    if (typeof protocol.createdAt.toDate === 'function') {
      return protocol.createdAt.toDate();
    }
    const createdDate = new Date(protocol.createdAt);
    if (!isNaN(createdDate.getTime())) {
      return createdDate;
    }
  }

  return new Date();
}

/**
 * Extracts specific days of week from text (0 = Sunday, 1 = Monday, ..., 6 = Saturday).
 * Uses word boundaries to avoid matching "mon" in "month" or "sat" in "saturation".
 */
export function extractDaysOfWeek(text) {
  if (!text) return [];
  const lower = text.toLowerCase();
  const days = new Set();

  // Sunday
  if (/\b(sun|sunday|sundays)\b/i.test(lower)) days.add(0);
  // Monday
  if (/\b(mon|monday|mondays)\b/i.test(lower)) days.add(1);
  // Tuesday
  if (/\b(tue|tues|tuesday|tuesdays)\b/i.test(lower)) days.add(2);
  // Wednesday
  if (/\b(wed|weds|wednesday|wednesdays)\b/i.test(lower)) days.add(3);
  // Thursday
  if (/\b(thu|thur|thurs|thursday|thursdays)\b/i.test(lower)) days.add(4);
  // Friday
  if (/\b(fri|friday|fridays)\b/i.test(lower)) days.add(5);
  // Saturday
  if (/\b(sat|saturday|saturdays)\b/i.test(lower)) days.add(6);

  // Common shorthand: MWF
  if (/\bmwf\b/i.test(lower)) {
    days.add(1);
    days.add(3);
    days.add(5);
  }

  return Array.from(days).sort((a, b) => a - b);
}

/**
 * Parses a free-text frequency string into a structured schedule descriptor.
 *
 * @param {string} frequencyStr - Raw frequency string (e.g. "every 3 days", "3 days apart", "E3D")
 * @returns {object} Structured schedule object:
 *   - { type: 'prn', description: string }
 *   - { type: 'interval', intervalDays: number, description: string }
 *   - { type: 'cycle', onDays: number, offDays: number, cycleLength: number, description: string }
 *   - { type: 'days_of_week', days: number[], description: string }
 *   - { type: 'interval_with_days', intervalDays: number, days: number[], description: string }
 *   - { type: 'weekdays', days: [1,2,3,4,5], description: string }
 *   - { type: 'weekends', days: [0,6], description: string }
 *   - { type: 'daily', intervalDays: 1, description: string }
 */
export function parseFrequencySchedule(frequencyStr = '') {
  const raw = (frequencyStr || '').trim();
  const lower = raw.toLowerCase();

  if (!lower) {
    return { type: 'daily', intervalDays: 1, description: 'Daily' };
  }

  // 1. PRN / As Needed Check
  if (
    lower.includes('prn') ||
    lower.includes('as needed') ||
    lower.includes('when needed') ||
    lower.includes('flexible')
  ) {
    return { type: 'prn', description: 'As Needed (PRN)' };
  }

  // 2. On / Off Cycles (e.g. "5 days on, 2 days off", "5 on 2 off", "4 on / 3 off")
  const cycleMatch = lower.match(
    /\b([a-z0-9-]+)\s*(?:days?|d)?\s*on\s*[,/]?\s*(?:and\s+)?([a-z0-9-]+)\s*(?:days?|d)?\s*off\b/i
  );
  if (cycleMatch) {
    const onDays = parseNumberToken(cycleMatch[1]);
    const offDays = parseNumberToken(cycleMatch[2]);
    if (onDays && onDays > 0 && offDays && offDays > 0) {
      return {
        type: 'cycle',
        onDays,
        offDays,
        cycleLength: onDays + offDays,
        description: `${onDays} days on, ${offDays} days off`
      };
    }
  }

  // 3. Clinical & Peptide shorthand: E<num>D or Q<num>D (e.g. E3D, Q4D, E7D, E10D)
  const eqMatch = lower.match(/\b[eq](\d+)d\b/i);
  if (eqMatch) {
    const days = parseInt(eqMatch[1], 10);
    if (days > 0) {
      return {
        type: 'interval',
        intervalDays: days,
        description: `Every ${days} days`
      };
    }
  }

  // EOD / QOD / Every other day (interval = 2)
  if (
    /\b(eod|qod)\b/i.test(lower) ||
    lower.includes('every other day') ||
    lower.includes('every second day') ||
    lower.includes('every 2nd day')
  ) {
    return {
      type: 'interval',
      intervalDays: 2,
      description: 'Every other day'
    };
  }

  // 4. "X days apart" and spacing variants
  // e.g. "3 days apart", "four days apart", "spaced 3 days apart", "space 3 days apart", "interval of 3 days", "3-day interval"
  const apartMatch =
    lower.match(/\b(?:spaced|space|taken)?\s*(\d+|[a-z]+(?:-[a-z]+)?)\s*(?:-|\s+)?(?:days?|d)\s+apart\b/i) ||
    lower.match(/\b(?:interval of|separated by|gap of)\s+(\d+|[a-z]+(?:-[a-z]+)?)\s*(?:-|\s+)?(?:days?|d)\b/i) ||
    lower.match(/\b(\d+|[a-z]+(?:-[a-z]+)?)\s*(?:-|\s+)(?:days?|d)\s+interval\b/i) ||
    lower.match(/\bevery\s+(\d+|[a-z]+(?:-[a-z]+)?)\s*(?:-|\s+)?(?:days?|d)\s+apart\b/i);

  if (apartMatch) {
    const days = parseNumberToken(apartMatch[1]);
    if (days && days > 0) {
      return {
        type: 'interval',
        intervalDays: days,
        description: `Every ${days} days (${days} days apart)`
      };
    }
  }

  // 5. "Every X days" and variants (digits, words, ordinals, and "every N" / "every Nth")
  // e.g. "every 3 days", "every four days", "every 7th day", "every third day", "every 3", "every 3rd", "every fourth", "every 4"
  const everyDayMatch =
    lower.match(/\b(?:every|each|once every|1x every)\s+(?:(\d+)\s*d\b|(\d+(?:st|nd|rd|th)?|[a-z]+(?:-[a-z]+)?)(?:\s+(?:days?|d)|-(?:days?|d))?)(?:\s*\(|\s*$|\b)/i);

  if (everyDayMatch) {
    const rawToken = everyDayMatch[1] || everyDayMatch[2];
    const days = parseNumberToken(rawToken);
    if (days && days > 0) {
      if (days === 1) {
        return { type: 'daily', intervalDays: 1, description: 'Daily' };
      }
      if (days === 2) {
        return { type: 'interval', intervalDays: 2, description: 'Every other day' };
      }
      return {
        type: 'interval',
        intervalDays: days,
        description: `Every ${days} days`
      };
    }
  }

  // 5b. Standalone number or interval shorthand e.g. "3", "3rd", "3 days", "3d", "four", "fourth", "7th"
  const standaloneMatch = lower.match(/^\s*(\d+(?:st|nd|rd|th)?|[a-z]+(?:-[a-z]+)?)\s*(?:days?|d)?\s*$/i);
  if (standaloneMatch) {
    const days = parseNumberToken(standaloneMatch[1]);
    if (days && days > 0) {
      if (days === 1) {
        return { type: 'daily', intervalDays: 1, description: 'Daily' };
      }
      if (days === 2) {
        return { type: 'interval', intervalDays: 2, description: 'Every other day' };
      }
      return {
        type: 'interval',
        intervalDays: days,
        description: `Every ${days} days`
      };
    }
  }

  // 6. Multi-week intervals (e.g. "every 2 weeks", "every two weeks", "bi-weekly", "every other week", "q2w", "eow")
  const everyWeekMatch = lower.match(/\b(?:every|each|once every|1x every)\s+([a-z0-9-]+)(?:\s*|-)(?:weeks?|wks?|w)\b/i);
  if (everyWeekMatch) {
    const weeks = parseNumberToken(everyWeekMatch[1]);
    if (weeks && weeks > 0) {
      const intervalDays = weeks * 7;
      const daysOfWeek = extractDaysOfWeek(lower);
      if (daysOfWeek.length > 0) {
        return {
          type: 'interval_with_days',
          intervalDays,
          days: daysOfWeek,
          description: `Every ${weeks} weeks on ${formatDaysOfWeekList(daysOfWeek)}`
        };
      }
      return {
        type: 'interval',
        intervalDays,
        description: `Every ${weeks} week${weeks > 1 ? 's' : ''}`
      };
    }
  }

  if (
    /\b(bi-?weekly|eow|q2w|e2w|every other week)\b/i.test(lower)
  ) {
    const daysOfWeek = extractDaysOfWeek(lower);
    if (daysOfWeek.length > 0) {
      return {
        type: 'interval_with_days',
        intervalDays: 14,
        days: daysOfWeek,
        description: `Every 2 weeks on ${formatDaysOfWeekList(daysOfWeek)}`
      };
    }
    return {
      type: 'interval',
      intervalDays: 14,
      description: 'Every 2 weeks (bi-weekly)'
    };
  }

  // 7. Weekdays & Weekends
  if (
    /\b(weekdays?|mon(?:day)?\s*-\s*fri(?:day)?|mon(?:day)?\s+through\s+fri(?:day)?)\b/i.test(lower)
  ) {
    return {
      type: 'weekdays',
      days: [1, 2, 3, 4, 5],
      description: 'Weekdays (Mon-Fri)'
    };
  }

  if (
    /\b(weekends?|sat(?:urday)?\s*-\s*sun(?:day)?)\b/i.test(lower)
  ) {
    return {
      type: 'weekends',
      days: [0, 6],
      description: 'Weekends (Sat-Sun)'
    };
  }

  // 8. Specific Days of Week
  const specificDays = extractDaysOfWeek(lower);
  if (specificDays.length > 0) {
    return {
      type: 'days_of_week',
      days: specificDays,
      description: formatDaysOfWeekList(specificDays)
    };
  }

  // 9. Daily / fallback
  if (
    /\b(daily|every\s*day|every\s+single\s+day|each\s+day|once\s+daily|1x\s+daily|ed|qd)\b/i.test(lower)
  ) {
    return {
      type: 'daily',
      intervalDays: 1,
      description: 'Daily'
    };
  }

  // Default fallback: Daily
  return {
    type: 'daily',
    intervalDays: 1,
    description: 'Daily'
  };
}

/**
 * Format a list of day indices (0-6) into a concise string e.g. "Mon, Wed, Fri"
 */
function formatDaysOfWeekList(days) {
  const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return days.map(d => names[d]).join(', ');
}

/**
 * Checks if a parsed frequency schedule is scheduled on targetDate relative to anchorDate.
 *
 * @param {object} schedule - Result of parseFrequencySchedule
 * @param {Date} targetDate - The date being queried
 * @param {Date} anchorDate - The protocol anchor date (startDate or createdAt or today)
 * @returns {boolean}
 */
export function isScheduleActiveOnDate(schedule, targetDate, anchorDate) {
  if (!schedule || !targetDate) return false;

  const dayOfWeek = targetDate.getDay();

  switch (schedule.type) {
    case 'prn':
      return false;

    case 'daily':
      return true;

    case 'weekdays':
      return dayOfWeek >= 1 && dayOfWeek <= 5;

    case 'weekends':
      return dayOfWeek === 0 || dayOfWeek === 6;

    case 'days_of_week':
      return schedule.days.includes(dayOfWeek);

    case 'interval': {
      const interval = schedule.intervalDays;
      if (!interval || interval <= 1) return true;
      const dayDiff = getDayDifference(anchorDate, targetDate);
      // Handles both positive and negative offsets symmetrically
      const mod = ((dayDiff % interval) + interval) % interval;
      return mod === 0;
    }

    case 'interval_with_days': {
      const interval = schedule.intervalDays;
      if (!schedule.days.includes(dayOfWeek)) {
        return false;
      }
      const dayDiff = getDayDifference(anchorDate, targetDate);
      const mod = ((dayDiff % interval) + interval) % interval;
      return mod === 0;
    }

    case 'cycle': {
      const { onDays, cycleLength } = schedule;
      if (!cycleLength || cycleLength <= 0) return true;
      const dayDiff = getDayDifference(anchorDate, targetDate);
      const mod = ((dayDiff % cycleLength) + cycleLength) % cycleLength;
      return mod < onDays;
    }

    default:
      return true;
  }
}
